import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePopper } from "react-popper";

import { MachineSubmenuSizeButton } from "@/sx-layout/components/plotmap/components/MachineSubmenuSizeButton";
import { useChangeMachineSize } from "@/sx-layout/components/plotmap/hooks/useChangeMachineSize";
import { Machine, MachineStatus } from "@/sx-layout/components/plotmap/models";

type Props = {
  referenceElement: HTMLElement;
  machine: Machine;
  scale: number;
  control: {
    isPast: boolean;
    isToday: boolean;
    isAdmin: boolean;
    isUser: boolean;
    isMachineFromOtherCompany: boolean;
    isDisableMachineStatusButton: boolean;
  };
  onEdit: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onDuplicateLater: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onDuplicateToday: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onCreateSubMachine: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onDelete: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onCreateKeepOutArea: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onChangeSize: (m: Machine) => void;
  onChangeStatus: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
};

export const MachineSubMenu: FC<Props> = ({
  referenceElement,
  machine,
  scale,
  control,
  onEdit,
  onDuplicateLater,
  onDuplicateToday,
  onCreateSubMachine,
  onDelete,
  onCreateKeepOutArea,
  onChangeSize,
  onChangeStatus,
}) => {
  const { t } = useTranslation();

  const [popperElement, setPopperElement] = useState(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "right",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 20],
        },
      },
    ],
  });

  const position = attributes?.popper?.["data-popper-placement"];

  const machineSize = useChangeMachineSize(machine, onChangeSize);

  // 連打防止用
  const [changingStatus, setChangingStatus] = useState(false);
  const throttle = (callback: () => void) => {
    if (changingStatus) return;
    setChangingStatus(true);
    callback();
    setTimeout(() => setChangingStatus(false), 1000);
  };

  const isUserAndOwnCompany = control.isUser && !control.isMachineFromOtherCompany;
  if (!(control.isAdmin || isUserAndOwnCompany)) {
    return <></>;
  }

  return (
    referenceElement && (
      <div
        id="layout-target-submenu"
        className="z-10"
        style={{
          ...styles.popper,
          transform: `${styles.popper?.transform ?? ""} scale(${scale})`,
          transformOrigin:
            position === "left" ? "right" : position === "top" ? "bottom" : position === "bottom" ? "top" : "left",
        }}
        ref={setPopperElement}
        {...attributes.popper}
      >
        <div id="layout-target-submenu-arrow" data-popper-arrow />
        <div className="bg-[#f3f2ef] p-[5px] w-[140px] font-bold rounded-[5px]">
          <ul id={`machine-submenu-${machine.machine_id}`} className="bg-[#f3f2ef]">
            {/* 編集 */}
            {!control.isPast && (control.isAdmin || isUserAndOwnCompany) ? (
              <li
                onClick={(e) => throttle(() => onEdit(e))}
                className="bg-[#fff] hover:bg-[#fff8ee] cursor-pointer rounded flex items-center justify-center p-[5px] text-[13px] mb-[5px]"
              >
                {t("edit")}
              </li>
            ) : null}
            {/* 後日複製 */}
            {control.isAdmin || isUserAndOwnCompany ? (
              <li
                onClick={(e) => throttle(() => onDuplicateLater(e))}
                className={
                  `bg-[#fff] hover:bg-[#fff8ee] cursor-pointer rounded flex items-center justify-center p-[5px] text-[13px]` +
                  (control.isPast ? "" : " mb-[5px]")
                }
              >
                {t("duplicate_heavy_machine_later")}
              </li>
            ) : null}
            {/* 当日複製 */}
            {!control.isPast && (control.isAdmin || isUserAndOwnCompany) ? (
              <li
                onClick={(e) => throttle(() => onDuplicateToday(e))}
                className={
                  `bg-[#fff] hover:bg-[#fff8ee] cursor-pointer rounded flex items-center justify-center p-[5px] text-[13px]` +
                  (control.isPast ? "" : " mb-[5px]")
                }
              >
                {t("duplicate_heavy_machine_today")}
              </li>
            ) : null}
            {/* 枝番追加 */}
            {!control.isPast && (control.isAdmin || isUserAndOwnCompany) ? (
              <li
                onClick={(e) => throttle(() => onCreateSubMachine(e))}
                className={
                  `bg-[#fff] hover:bg-[#fff8ee] cursor-pointer rounded flex items-center justify-center p-[5px] text-[13px]` +
                  (control.isPast ? "" : " mb-[5px]")
                }
              >
                {t("add_branch")}
              </li>
            ) : null}
            {/* 立入禁止エリア作成 */}
            {!control.isPast && (control.isAdmin || isUserAndOwnCompany) ? (
              <li className="bg-[#fff] hover:bg-[#fff8ee] cursor-pointer rounded flex items-center justify-center p-[5px] text-[13px] mb-[5px]">
                <button
                  onClick={(e) => throttle(() => onCreateKeepOutArea(e))}
                  className="w-[100%] disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!!machine.keepout_area_id}
                >
                  {t("create_keep_out_area")}
                </button>
              </li>
            ) : null}
            {/* サイズ変更 */}
            {!control.isPast && (control.isAdmin || isUserAndOwnCompany) ? (
              <li className="cursor-pointer rounded flex items-center justify-between p-[2px] text-[13px] mb-[5px]">
                <MachineSubmenuSizeButton onClick={machineSize.decrease} disabled={machineSize.size <= 0.5}>
                  <img src="./img/icon-minus-black.svg" className="w-[16px] h-[16px]" />
                </MachineSubmenuSizeButton>
                <div className="leading-[1.2rem] text-center">
                  {t("size")}
                  <br />
                  <span className="text-[1rem]">{Math.round(machineSize.size * 100)}%</span>
                </div>
                <MachineSubmenuSizeButton onClick={machineSize.increase} disabled={2 <= machineSize.size}>
                  <img src="./img/icon-plus-black.svg" className="w-[16px] h-[16px]" />
                </MachineSubmenuSizeButton>
              </li>
            ) : null}
            {/* 作業完了 / 再稼働 */}
            {control.isToday && (control.isAdmin || isUserAndOwnCompany) ? (
              <li className="bg-[#fff] hover:bg-[#fff8ee] cursor-pointer rounded flex items-center justify-center p-[5px] text-[13px] mb-[5px]">
                <button
                  onClick={(e) => throttle(() => onChangeStatus(e))}
                  className="w-[100%] disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={control.isDisableMachineStatusButton}
                >
                  {machine.status === MachineStatus.STAND_BY && t("resume_machine_operation")}
                  {machine.status === MachineStatus.RUNNING && t("complete_process")}
                </button>
              </li>
            ) : null}
            {/* 削除 */}
            {!control.isPast && (control.isAdmin || isUserAndOwnCompany) ? (
              <li
                onClick={(e) => throttle(() => onDelete(e))}
                className="bg-[#ffdddd] hover:bg-[#f8d0d0] cursor-pointer rounded flex items-center justify-center p-[5px] text-[13px]"
              >
                {t("delete")}
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    )
  );
};
